import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography, Paper, Button, Alert, Chip, CircularProgress } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import { ChallengeTableChallenge } from "../../../../api/types/dtos/challenge/ChallengeDto";
import { ReportCell, CellMapping, ChallengeFilter } from "../types/reportBuilder.types";
/**
 * @deprecated Local CSV generation imports — kept for future removal.
 * Do not add new usage of these utilities.
 */
import { processCellValue, generateCSV, applyInitialFilters } from "../utils/reportBuilder.utils";
import { AnswerDto } from "../../../../api/services/answer.service";
import { useRequestReport, useDownloadReport, useReport } from "../../../../api/hooks/useReports";
import { ReportDto, ReportStatus } from "../../../../api/types/dtos/report";

interface ProcessingStepProps {
  cells: ReportCell[];
  cellMappings: CellMapping[];
  challenges: ChallengeTableChallenge[];
  initialFilters?: ChallengeFilter[];
  answersMap?: Record<string, AnswerDto[]>;
  onComplete: () => void;
  templateId?: string | null;
  onReportRequested?: (reportId: string) => void;
}

const STATUS_LABELS: Record<ReportStatus, string> = {
  requested: "Solicitado",
  in_process: "En proceso",
  available: "Disponible",
  failed: "Fallido",
};

const STATUS_COLORS: Record<ReportStatus, "default" | "info" | "success" | "error"> = {
  requested: "default",
  in_process: "info",
  available: "success",
  failed: "error",
};

/**
 * @deprecated Local CSV processing state and logic — kept for future removal.
 */
const useLocalProcessing = (
  _challenges: ChallengeTableChallenge[],
  _initialFilters: ChallengeFilter[] | undefined,
  _sortedCells: ReportCell[],
  _cellMappings: CellMapping[],
  _answersMap: Record<string, AnswerDto[]> | undefined
) => {
  const [localCsvContent, setLocalCsvContent] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [localProcessing, setLocalProcessing] = useState(false);
  const [localProgress, setLocalProgress] = useState(0);

  const handleLocalProcess = useCallback(async () => {
    setLocalProcessing(true);
    setLocalProgress(0);
    setLocalError(null);
    setLocalCsvContent(null);

    try {
      const filteredChallenges = _initialFilters
        ? applyInitialFilters(_challenges, _initialFilters)
        : _challenges;

      const total = filteredChallenges.length;
      const rows: string[][] = [];

      for (let i = 0; i < filteredChallenges.length; i++) {
        const challenge = filteredChallenges[i];
        const row: string[] = [];

        for (const cell of _sortedCells) {
          const mapping = _cellMappings.find((m) => m.cellId === cell.id);
          if (mapping) {
            const processed = processCellValue(challenge, mapping, _answersMap);
            row.push(processed.value || "");
          } else {
            row.push("");
          }
        }

        rows.push(row);
        setLocalProgress(((i + 1) / total) * 100);
      }

      const headers = _sortedCells.map((c) => c.name);
      const processedRows = rows.map((row) =>
        row.map((value, index) => ({
          cellId: _sortedCells[index]?.id || "",
          value: value || "",
        }))
      );
      const csv = generateCSV(headers, processedRows);
      setLocalCsvContent(csv);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Error al procesar el reporte";
      setLocalError(message);
    } finally {
      setLocalProcessing(false);
    }
  }, [_challenges, _initialFilters, _sortedCells, _cellMappings, _answersMap]);

  return { localCsvContent, localError, localProcessing, localProgress, handleLocalProcess };
};

const ProcessingStep: React.FC<ProcessingStepProps> = ({
  cells,
  cellMappings,
  challenges,
  initialFilters,
  answersMap,
  onComplete,
  templateId,
  onReportRequested,
}) => {
  const [mode, setMode] = useState<"idle" | "backend">("idle");
  const [reportId, setReportId] = useState<string | null>(null);
  /** @deprecated local processing ref — kept for future removal */
  const _localProcessStarted = useRef(false);

  const requestReport = useRequestReport();
  const downloadReport = useDownloadReport();
  const { data: reportData, refetch: refetchReport } = useReport(reportId || "");

  const sortedCells = React.useMemo(() => {
    return [...cells].sort((a, b) => a.order - b.order);
  }, [cells]);

  /** @deprecated Local CSV processing hook call — kept for future removal */
  const _localProcessing = useLocalProcessing(
    challenges,
    initialFilters,
    sortedCells,
    cellMappings,
    answersMap
  );

  // Poll for report status when using backend mode
  useEffect(() => {
    if (mode !== "backend" || !reportId) return;

    const status = (reportData as ReportDto | undefined)?.status;
    if (status === "available" || status === "failed") return;

    const interval = setInterval(() => {
      refetchReport();
    }, 5000);

    return () => clearInterval(interval);
  }, [mode, reportId, reportData, refetchReport]);

  const handleBackendRequest = useCallback(async () => {
    const targetOrgIds = [...new Set(challenges.map((c) => c.inspectedOrgId))];

    try {
      const result = await requestReport.mutateAsync({
        reportTemplateId: templateId!,
        name: `Reporte ${new Date().toLocaleDateString()}`,
        targetOrganizationIds: targetOrgIds,
      });
      setMode("backend");
      setReportId(result.id);
      onReportRequested?.(result.id);
    } catch {
      setMode("idle");
    }
  }, [templateId, challenges, requestReport, onReportRequested]);

  const handleDownload = useCallback(async () => {
    if (!reportId || (reportData as ReportDto | undefined)?.status !== "available") return;
    try {
      const url = await downloadReport.mutateAsync(reportId);
      if (url) {
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `reporte_${reportId}.csv`);
        link.setAttribute("target", "_blank");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch {
      // Error handled by mutation state
    }
  }, [reportId, reportData, downloadReport]);

  const handleStart = () => {
    if (templateId) {
      handleBackendRequest();
    }
  };

  const backendStatus = (reportData as ReportDto | undefined)?.status;
  const isBackendProcessing =
    mode === "backend" &&
    backendStatus !== undefined &&
    backendStatus !== "available" &&
    backendStatus !== "failed";

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Procesamiento y Exportación
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        El reporte será generado por el servidor. Podrás descargarlo cuando esté listo.
      </Typography>

      {mode === "idle" && (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          {!templateId ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Debes guardar la plantilla antes de procesar el reporte.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Guarda la plantilla usando el ícono de guardado en la parte superior y luego vuelve
                a este paso.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {challenges.length} organizaciones serán incluidas en el reporte.
              </Typography>
              <Button
                variant="contained"
                onClick={handleStart}
                sx={{ mt: 2 }}
                disabled={challenges.length === 0 || requestReport.isPending}
              >
                {requestReport.isPending ? <CircularProgress size={20} /> : "Procesar Reporte"}
              </Button>
            </>
          )}
        </Paper>
      )}

      {mode === "backend" && reportId && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Typography variant="body2">Estado:</Typography>
            <Chip
              label={STATUS_LABELS[backendStatus as ReportStatus] || backendStatus}
              color={STATUS_COLORS[backendStatus as ReportStatus] || "default"}
            />
            {isBackendProcessing && (
              <Button size="small" startIcon={<RefreshIcon />} onClick={() => refetchReport()}>
                Actualizar
              </Button>
            )}
          </Box>

          {isBackendProcessing && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                El reporte se está generando en el servidor...
              </Typography>
            </Box>
          )}

          {backendStatus === "available" && (
            <Alert severity="success" sx={{ mb: 2 }}>
              El reporte se ha procesado exitosamente. Puedes descargarlo ahora.
            </Alert>
          )}

          {backendStatus === "failed" && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error al generar el reporte. Intenta nuevamente.
            </Alert>
          )}

          {requestReport.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error al solicitar el reporte.
            </Alert>
          )}

          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            {backendStatus === "available" && (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                disabled={downloadReport.isPending}
              >
                {downloadReport.isPending ? <CircularProgress size={20} /> : "Descargar CSV"}
              </Button>
            )}
            <Button variant="outlined" onClick={onComplete}>
              Crear Nuevo Reporte
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default ProcessingStep;
